
import React from 'react';
import { useApp } from '../store';
import { Link, useNavigate } from 'react-router-dom';
import { RequestStatus } from '../types';
import { Plus, Clock, MapPin, DollarSign, ChevronRight, ChevronLeft, Hammer, Compass } from 'lucide-react';

const StatusBadge: React.FC<{ status: RequestStatus }> = ({ status }) => {
  const colors = {
    [RequestStatus.POSTED]: 'bg-blue-50 text-blue-600 border-blue-100',
    [RequestStatus.OPEN]: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    [RequestStatus.CLARIFYING]: 'bg-amber-50 text-amber-600 border-amber-100',
    [RequestStatus.LOCKED]: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    [RequestStatus.EN_ROUTE]: 'bg-purple-50 text-purple-600 border-purple-100',
    [RequestStatus.ARRIVED]: 'bg-pink-50 text-pink-600 border-pink-100',
    [RequestStatus.COMPLETED]: 'bg-stone-100 text-stone-500 border-stone-200',
    [RequestStatus.CANCELLED]: 'bg-rose-50 text-rose-600 border-rose-100',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${colors[status]}`}>
      {status}
    </span>
  );
};

const RequesterDashboard: React.FC = () => {
  const { requests, currentUser } = useApp();
  const navigate = useNavigate();
  const myRequests = requests.filter(r => r.requesterId === currentUser?.id);

  return (
    <div className="max-w-4xl mx-auto space-y-12 px-4 reveal">
      <header className="flex justify-between items-end">
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-stone-400 mb-2">
            <Compass size={18} />
            <span className="text-[10px] font-black uppercase tracking-[0.4em]">Workshop Floor</span>
          </div>
          <h2 className="text-5xl font-black text-stone-900 tracking-tight font-serif italic">Your Ledger</h2>
        </div>
        <Link 
          to="/create" 
          className="bg-stone-900 text-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl hover:bg-amber-600 transition-all hover:rotate-90 group"
        >
          <Plus size={28} />
        </Link>
      </header>

      {myRequests.length === 0 ? (
        <div className="bg-white border border-black/5 rounded-[3rem] p-24 text-center soft-shadow">
          <div className="w-20 h-20 bg-stone-50 rounded-3xl flex items-center justify-center mx-auto mb-8 text-stone-200">
            <Hammer size={40} />
          </div>
          <h3 className="text-3xl font-black text-stone-900 mb-4 font-serif italic">The Shop is Open</h3>
          <p className="text-stone-500 mb-10 max-w-sm mx-auto text-lg leading-relaxed">Commission your first project. Whether it's a lock, a dress, or a custom desk.</p>
          <Link to="/create" className="inline-block bg-stone-900 text-white px-10 py-5 rounded-2xl font-bold text-base hover:bg-amber-600 transition-all shadow-xl">
            Open a Request
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {myRequests.map(request => (
            <Link 
              key={request.id} 
              to={`/request/${request.id}`}
              className="premium-card p-8 rounded-[2.5rem] flex flex-col justify-between"
            >
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 px-4 py-1 rounded-full border border-amber-100">{request.category}</span>
                  <StatusBadge status={request.status} />
                </div>
                
                <h3 className="text-2xl font-black text-stone-900 line-clamp-2 font-serif group-hover:text-amber-700 transition-colors leading-tight italic">
                  "{request.text}"
                </h3>

                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-black/5">
                  <div className="flex items-center gap-2 text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                    <Clock size={14} className="text-amber-500" /> {request.urgency}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                    <DollarSign size={14} className="text-emerald-500" /> {request.estimatedPrice}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between text-stone-300">
                <div className="flex items-center gap-2">
                  <MapPin size={14} />
                  <span className="text-[9px] font-black uppercase tracking-[0.2em]">{request.location?.address.split(',')[0]}</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-stone-900 group-hover:bg-amber-500 group-hover:text-white transition-all">
                  <ChevronRight size={16} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequesterDashboard;
